import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/lib/hooks/use-toast';
import { useCustomerStore } from '../store/customer-store';
import { Customer, CustomerSegment } from '../types/customer.types';

const SEGMENTS: { value: CustomerSegment; label: string }[] = [
  { value: 'ENTERPRISE', label: 'Enterprise' },
  { value: 'SMB', label: 'PyME' },
  { value: 'GOBIERNO', label: 'Gobierno' },
  { value: 'STARTUP', label: 'Startup' },
];

interface QuickCreateCustomerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (customer: Customer) => void;
  initialName?: string;
}

export function QuickCreateCustomerDialog({
  open,
  onOpenChange,
  onCreated,
  initialName = '',
}: QuickCreateCustomerDialogProps) {
  const { toast } = useToast();
  const { createCustomer } = useCustomerStore();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [form, setForm] = useState({
    name: initialName,
    code: '',
    email: '',
    phone: '',
    segment: 'SMB' as CustomerSegment,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when dialog opens
  const handleOpenChange = (value: boolean) => {
    if (value) {
      setForm({
        name: initialName,
        code: '',
        email: '',
        phone: '',
        segment: 'SMB',
      });
      setErrors({});
    }
    onOpenChange(value);
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs.name = 'El nombre es obligatorio';
    if (!form.code.trim()) errs.code = 'El código es obligatorio';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errs.email = 'Email inválido';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const customer = await createCustomer({
        name: form.name.trim(),
        code: form.code.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        segment: form.segment,
      });

      toast({
        title: 'Cliente creado',
        description: `${customer.name} se creó exitosamente`,
        variant: 'success',
      });

      onCreated(customer);
      onOpenChange(false);
    } catch (error: any) {
      const msg = error.response?.data?.error || error.message || 'Error al crear cliente';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Crear Cliente Rápido</DialogTitle>
          <DialogDescription>
            Crea un nuevo cliente sin salir de la cotización. Podrás completar los demás datos
            después.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="qc-name">Nombre *</Label>
            <Input
              id="qc-name"
              value={form.name}
              onChange={(e) => {
                setForm({ ...form, name: e.target.value });
                setErrors((prev) => {
                  const n = { ...prev };
                  delete n.name;
                  return n;
                });
              }}
              placeholder="Nombre del cliente"
              autoFocus
            />
            {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="qc-code">Código *</Label>
              <Input
                id="qc-code"
                value={form.code}
                onChange={(e) => {
                  setForm({ ...form, code: e.target.value });
                  setErrors((prev) => {
                    const n = { ...prev };
                    delete n.code;
                    return n;
                  });
                }}
                placeholder="CL-001"
              />
              {errors.code && <p className="text-xs text-destructive">{errors.code}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="qc-segment">Segmento</Label>
              <Select
                value={form.segment}
                onValueChange={(value) => setForm({ ...form, segment: value as CustomerSegment })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SEGMENTS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="qc-email">Email</Label>
              <Input
                id="qc-email"
                type="email"
                value={form.email}
                onChange={(e) => {
                  setForm({ ...form, email: e.target.value });
                  setErrors((prev) => {
                    const n = { ...prev };
                    delete n.email;
                    return n;
                  });
                }}
                placeholder="email@empresa.com"
              />
              {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="qc-phone">Teléfono</Label>
              <Input
                id="qc-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+57 300 123 4567"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creando...
                </>
              ) : (
                'Crear Cliente'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
